// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Alert, Button, Modal, Spinner, TablePagination } from '@loa/ui-components';
import { formatDate } from '@loa/theme';
import { croApi } from '../../api/croApi';
import type { CroCompanyDetail, CroCompanyHolding } from '../../api/croApi';

const PAGE_SIZE = 5;

function money(amount: number | null | undefined, currency = 'EUR'): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-IE', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
  } catch {
    return `${Math.round(amount).toLocaleString()} ${currency}`;
  }
}

/**
 * Flat, de-duplicated list of the companies/businesses behind a relationship.
 * A summary card leads with the company count + total holding worth; the list
 * shows five at a time with pagination; each row leads with the household's
 * holding worth (stake value) and a Details button opening the full company
 * record. Backs the relationship Overview "Corporate Holdings" dashboard card.
 */
export function CroCompanyList({ companies }: { companies: CroCompanyHolding[] }) {
  const [selected, setSelected] = useState<CroCompanyHolding | null>(null);
  const [page, setPage] = useState(1);           // 1-indexed

  const currency = companies.find((c) => c.currencyCode)?.currencyCode ?? 'EUR';
  const totalWorth = useMemo(
    () => companies.reduce((sum, c) => sum + (c.stakeValue ?? 0), 0),
    [companies],
  );
  const hasAnyWorth = companies.some((c) => c.stakeValue != null);

  const totalPages = Math.max(1, Math.ceil(companies.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const pageItems = companies.slice(start, start + PAGE_SIZE);

  return (
    <div className="flex flex-col gap-md">
      {/* Summary card */}
      <div className="flex flex-row items-center justify-between gap-md rounded-card bg-lilac-06 p-md">
        <div className="flex flex-col">
          <span className="text-h5 font-semibold leading-none">{companies.length}</span>
          <span className="text-body-04 text-grey-04">
            {companies.length === 1 ? 'company' : 'companies'}
          </span>
        </div>
        {hasAnyWorth && (
          <div className="flex flex-col items-end">
            <span className="text-body-01 font-semibold">{money(totalWorth, currency)}</span>
            <span className="text-body-04 text-grey-04">total holding worth</span>
          </div>
        )}
      </div>

      <div className="flex flex-col gap-xs">
        {pageItems.map((c) => (
          <div key={c.companyKey || c.companyNumber}
               className="flex flex-row items-center justify-between gap-md border border-grey-06 rounded-input p-md bg-white">
            <div className="flex flex-col min-w-0">
              <span className="text-body-02 font-semibold truncate">{c.name}</span>
              <span className="text-body-04 text-grey-04 truncate">
                {c.roles.join(' · ')}
                {c.status ? ` · ${c.status}` : ''}
                {c.entityType === 'BUSINESS' ? ' · Business name' : ` · No. ${c.companyNumber}`}
              </span>
            </div>
            <div className="flex flex-row items-center gap-md shrink-0">
              <div className="flex flex-col items-end">
                <span className="text-body-01 font-semibold">{money(c.stakeValue, c.currencyCode ?? 'EUR')}</span>
                <span className="text-body-04 text-grey-04">
                  {c.sharePercent != null && c.netAssets != null
                    ? `${c.sharePercent}% of ${money(c.netAssets, c.currencyCode ?? 'EUR')}`
                    : 'Holding worth'}
                </span>
              </div>
              <Button variant="outlined" size="sm" onClick={() => setSelected(c)}>Details</Button>
            </div>
          </div>
        ))}
      </div>

      {companies.length > PAGE_SIZE && (
        <TablePagination
          config={{
            currentPage: safePage,
            totalPages,
            itemsPerPage: PAGE_SIZE,
            itemsPerPageOptions: [PAGE_SIZE],   // fixed at 5 per page
            onPageChange: setPage,
          }}
        />
      )}

      {selected && (
        <CompanyDetailModal company={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function CompanyDetailModal({ company, onClose }: { company: CroCompanyHolding; onClose: () => void }) {
  const [detail, setDetail] = useState<CroCompanyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    croApi.getCompany(company.companyKey, company.name, company.entityType)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [company.companyKey, company.name, company.entityType]);

  return (
    <Modal open onClose={onClose} size="large">
      <Modal.Header title={company.name} onClose={onClose} />
      <Modal.Content>
        {loading && <div className="flex justify-center p-xl"><Spinner /></div>}
        {error && <Alert variant="error" header="Couldn't load company details">{error}</Alert>}
        {detail && <CompanyDetail detail={detail} holding={company} />}
      </Modal.Content>
    </Modal>
  );
}

function CompanyDetail({ detail, holding }: { detail: CroCompanyDetail; holding: CroCompanyHolding }) {
  const currency = holding.currencyCode ?? 'EUR';
  return (
    <div className="flex flex-col gap-lg">
      {/* Worth highlight */}
      <div className="flex flex-row items-center justify-between gap-md rounded-card bg-lilac-06 p-md">
        <div className="flex flex-col">
          <span className="text-body-04 text-grey-04 uppercase">Holding worth</span>
          <span className="text-h5 font-semibold">{money(holding.stakeValue, currency)}</span>
        </div>
        <div className="flex flex-col items-end text-body-03 text-grey-04">
          <span>{holding.roles.join(' · ')}</span>
          {holding.sharePercent != null && <span>{holding.sharePercent}% shareholding</span>}
        </div>
      </div>

      {/* Facts */}
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-lg gap-y-md">
        <Field label="CRO number" value={detail.companyNumber} />
        <Field label="Status" value={detail.status} />
        <Field label="Type" value={detail.type} />
        <Field label="Registered" value={detail.registeredOn ? formatDate(detail.registeredOn) : undefined} />
        {detail.dissolvedOn && <Field label="Dissolved" value={formatDate(detail.dissolvedOn)} />}
        <Field label="Net assets (latest)" value={holding.netAssets != null ? money(holding.netAssets, currency) : undefined} />
        <div className="sm:col-span-2">
          <Field label="Registered address"
                 value={[detail.address, detail.county, detail.eircode].filter(Boolean).join(', ')} />
        </div>
      </dl>

      <Section title={`Officers${detail.officers.length ? ` (${detail.officers.length})` : ''}`} show={detail.officers.length > 0}>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-lg gap-y-xs text-body-02">
          {detail.officers.map((o) => (
            <li key={o.providerPersonId + o.name} className="flex flex-row justify-between gap-sm border-b border-grey-06 py-xs">
              <span className="truncate">{o.name}</span>
              <span className="text-grey-04 shrink-0">{o.role ?? '—'}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title={`Shareholders${detail.shareholders.length ? ` (${detail.shareholders.length})` : ''}`} show={detail.shareholders.length > 0}>
        <ul className="flex flex-col gap-xs text-body-02">
          {detail.shareholders.map((s, i) => (
            <li key={s.name + i} className="flex flex-row justify-between gap-sm border-b border-grey-06 py-xs">
              <span className="truncate">{s.name}</span>
              <span className="text-grey-04 shrink-0">
                {s.percent != null ? `${s.percent}%` : ''}{s.type ? ` ${s.type}` : ''}
              </span>
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Financials" show={detail.financials.length > 0}>
        <div className="overflow-x-auto">
          <table className="w-full text-body-02 border-collapse">
            <thead>
              <tr className="text-left text-grey-04">
                <th className="py-xs pr-md">Year end</th><th className="pr-md">Turnover</th>
                <th className="pr-md">Net assets</th><th>Cash</th>
              </tr>
            </thead>
            <tbody>
              {detail.financials.map((f, i) => (
                <tr key={f.yearEnd ?? i} className="border-t border-grey-06">
                  <td className="py-xs pr-md whitespace-nowrap">{f.yearEnd ? formatDate(f.yearEnd) : '—'}</td>
                  <td className="pr-md whitespace-nowrap">{money(f.turnover, currency)}</td>
                  <td className="pr-md whitespace-nowrap">{money(f.netAssets, currency)}</td>
                  <td className="whitespace-nowrap">{money(f.cash, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {detail.stale && (
        <p className="text-body-04 text-grey-04">Showing a cached copy — a refresh could not be completed.</p>
      )}
    </div>
  );
}

function Section({ title, show, children }: { title: string; show: boolean; children: ReactNode }) {
  if (!show) return null;
  return (
    <section className="flex flex-col gap-xs">
      <h4 className="text-body-04 font-semibold text-grey-04 uppercase">{title}</h4>
      {children}
    </section>
  );
}

function Field({ label, value }: { label: string; value?: string }) {
  if (!value) return null;
  return (
    <div className="flex flex-col">
      <dt className="text-body-04 text-grey-04">{label}</dt>
      <dd className="text-body-02">{value}</dd>
    </div>
  );
}
